import { z, ZodType } from 'zod'

import { SchoolLevelEnum } from '../../../lib/types/enums/common/school-level.enum'
import { AccomodationCategoryEnum } from '../../../lib/types/enums/product/accomodation-category.enum'
import { ProductTypeEnum } from '../../../lib/types/enums/product/product-type.enum'
import { Journey } from '../../../lib/types/models/product/journey'
import { JourneyInput } from '../../../lib/types/models/product/journey-input'
import { ProductInput } from '../../../lib/types/models/product/product-input'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const getDurationInDaysString = (duration: number) => {
  if (duration === 0) return '0 jour / 0 nuit'
  return `${duration} jours / ${duration - 1} nuits`
}

export const mapJourneyToInput = (journey: Journey): JourneyInput => {
  return {
    mainOptionId: journey.mainOptionId,
    mainOptionPrice: journey.mainOptionPrice,
    secondaryOptions: journey.secondaryOptions.map((so) => ({
      id: so.id,
      optionId: so.optionId,
      price: so.price,
      age: so.age
    })),
    fa1eIncluded: journey.fa1eIncluded,
    fa1ePrice: journey.fa1ePrice,
    fa2eIncluded: journey.fa2eIncluded,
    fa2ePrice: journey.fa2ePrice,
    isMandatoryUnderAge: !!journey.mandatoryUnderAge,
    mandatoryUnderAge: journey.mandatoryUnderAge
  }
}

const productFormBaseSchema = z.object({
  name: createStringSchema('Nom du produit'),
  accomodationCategory: createEnumSchema({
    fieldName: "Type d'hébergement",
    enumType: AccomodationCategoryEnum
  }),
  countryId: createNumberSchema('pays', { isInteger: true }),
  formulaId: createNumberSchema('formule', {
    isInteger: true,
    errorMessage: 'Veuillez saisir une formule valide'
  }),
  type: createEnumSchema({ fieldName: 'Type de produit', enumType: ProductTypeEnum }),
  minAge: createNumberSchema("l'âge minimum", { isInteger: true, nullish: true }),
  maxAge: createNumberSchema("l'âge maximum", { isInteger: true, nullish: true }),
  splitAge: createNumberSchema("l'âge de séparation", { nullish: true }),
  minSchoolLevel: createEnumSchema({
    fieldName: 'Niveau scolaire minimum',
    enumType: SchoolLevelEnum
  }),
  maxSchoolLevel: createEnumSchema({
    fieldName: 'Niveau scolaire maximum',
    enumType: SchoolLevelEnum,
    nullish: true
  }),
  numberOfHoursPerDay: createNumberSchema("le nombre d'heures de cours par jour", {
    isInteger: true,
    nullish: true
  }),
  numberOfHoursPerWeek: createNumberSchema("le nombre d'heures de cours par semaine", {
    isInteger: true,
    nullish: true
  }),
  minNumberOfStudents: createNumberSchema("le nombre minimum d'élèves", {
    isInteger: true,
    nullish: true
  }),
  maxNumberOfStudents: createNumberSchema("le nombre maximum d'élèves", {
    isInteger: true,
    nullish: true
  }),
  numberOfFullDay: createNumberSchema('le nombre de sorties à la journée', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  numberOfTeachingRoom: createNumberSchema('le nombre de salles de cours', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  numberOfTeflPerDay: createNumberSchema('le nombre de TEFL - Sortie à la journée', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  numberOfTeflWithActivityOnly: createNumberSchema('le nombre de TEFL - Activité uniquement', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  numberOfTeflWithTuitionAndActivity: createNumberSchema('le nombre de TEFL - Cours et activité', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  numberOfTeflWithTuitionOnly: createNumberSchema('le nombre de TEFL - Cours uniquement', {
    isInteger: true,
    isAtLeastZero: true,
    nullish: true
  }),
  noTransportGuide: createStringSchema(
    "Informations d'arrivée et de départ sur le lieu de séjour",
    { nullish: true }
  )
})

export const requiresNumberOfHoursPerWeek = (type?: ProductTypeEnum) =>
  type === ProductTypeEnum.ONE_TO_ONE || type === ProductTypeEnum.ONE_TO_ONE_INDIVIDUAL

const productFormOneToOneSchema = z.object({
  numberOfHoursPerWeek: createNumberSchema("le nombre d'heures de cours par semaine", {
    isInteger: true,
    isPositive: true
  })
})

export const productFormSchema: ZodType<ProductInput> = z.discriminatedUnion('type', [
  productFormBaseSchema.extend({ type: z.literal(ProductTypeEnum.NORMAL) }),
  productFormBaseSchema.extend({ type: z.literal(ProductTypeEnum.CLUB_FOUR) }),
  productFormBaseSchema
    .extend({ type: z.literal(ProductTypeEnum.ONE_TO_ONE) })
    .merge(productFormOneToOneSchema),
  productFormBaseSchema
    .extend({ type: z.literal(ProductTypeEnum.ONE_TO_ONE_INDIVIDUAL) })
    .merge(productFormOneToOneSchema)
])
